import React from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Container, 
  Paper,
  Alert 
} from '@mui/material';
import { 
  ErrorOutline, 
  Refresh, 
  Home,
  WifiOff,
  Security,
  Error
} from '@mui/icons-material';

interface ErrorPageProps {
  error?: {
    type: 'NETWORK_ERROR' | 'SERVER_ERROR' | 'AUTH_ERROR' | 'UNKNOWN_ERROR';
    message: string;
    statusCode?: number;
  };
  onRetry?: () => void;
  onGoHome?: () => void;
}

const ErrorPage: React.FC<ErrorPageProps> = ({ 
  error,
  onRetry,
  onGoHome
}) => {
  
  const errorType = error?.type || 'UNKNOWN_ERROR';
  const message = error?.message || 'เกิดข้อผิดพลาดไม่ทราบสาเหตุ';

  const getErrorContent = () => {
    switch (errorType) {
      case 'NETWORK_ERROR':
        return {
          icon: <WifiOff sx={{ fontSize: 80, color: 'warning.main' }} />,
          title: 'ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้',
          description: 'กรุณาตรวจสอบการเชื่อมต่ออินเทอร์เน็ตของคุณ',
          severity: 'warning' as const
        };
      case 'AUTH_ERROR':
        return {
          icon: <Security sx={{ fontSize: 80, color: 'error.main' }} />,
          title: 'หมดอายุการเข้าสู่ระบบ',
          description: 'กรุณาเข้าสู่ระบบใหม่',
          severity: 'error' as const
        };
      case 'SERVER_ERROR':
        return {
          icon: <Error sx={{ fontSize: 80, color: 'error.main' }} />,
          title: 'เซิร์ฟเวอร์มีปัญหา',
          description: 'ระบบกำลังมีปัญหา กรุณาลองใหม่ในภายหลัง',
          severity: 'error' as const
        };
      default:
        return {
          icon: <ErrorOutline sx={{ fontSize: 80, color: 'error.main' }} />,
          title: 'เกิดข้อผิดพลาด',
          description: 'ระบบเกิดข้อผิดพลาดที่ไม่ทราบสาเหตุ',
          severity: 'error' as const
        };
    }
  };

  const errorContent = getErrorContent();

  const handleRefresh = () => {
    if (onRetry) {
      onRetry();
    } else {
      window.location.reload();
    }
  };

  const handleGoHome = () => {
    if (onGoHome) {
      onGoHome();
    } else {
      window.location.href = '/';
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 4
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            textAlign: 'center',
            width: '100%'
          }}
        >
          <Box sx={{ mb: 3 }}>
            {errorContent.icon}
          </Box>
          
          <Typography variant="h4" component="h1" gutterBottom>
            {errorContent.title}
          </Typography>
          
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {errorContent.description}
          </Typography>

          {message && (
            <Alert severity={errorContent.severity} sx={{ mb: 3, textAlign: 'left' }}>
              <Typography variant="body2">
                รายละเอียด: {message}
              </Typography>
            </Alert>
          )}

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button
              variant="contained"
              startIcon={<Refresh />}
              onClick={handleRefresh}
              color="primary"
            >
              ลองใหม่
            </Button>
            
            <Button
              variant="outlined"
              startIcon={<Home />}
              onClick={handleGoHome}
              color="primary"
            >
              กลับหน้าหลัก
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default ErrorPage;
